import { useState, useRef } from "react";
import { api, downloadUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT, useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, ImageIcon, ArrowLeft, ArrowRight, X, Download, Loader2 } from "lucide-react";
import BuyCreditsButton from "../components/BuyCreditsButton";

export default function ImageToPdf() {
  const { user, refresh } = useAuth();
  const t = useT();
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (files) => {
    const arr = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    const next = arr.map(f => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      url: URL.createObjectURL(f),
    }));
    setItems((cur) => [...cur, ...next]);
  };

  const removeAt = (id) => setItems((cur) => cur.filter(x => x.id !== id));

  const move = (idx, delta) => {
    setItems((cur) => {
      const next = [...cur];
      const to = idx + delta;
      if (to < 0 || to >= next.length) return cur;
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const convert = async () => {
    if (items.length === 0) return toast.error(t("toast.addImages"));
    if ((user?.credits ?? 0) < 1) return toast.error(t("toast.needCredits"));
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      items.forEach(x => fd.append("files", x.file, x.file.name));
      const r = await api.post("/tools/image-to-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(r.data);
      toast.success(t("toast.pdfReady"));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="paper-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="image-to-pdf-page">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="pill mb-3">{t("img.badge")}</div>
            <h1 className="font-serif-display text-4xl font-bold">{t("img.title")}</h1>
            <p className="text-[#475569] mt-1">{t("img.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="pill">{user?.credits ?? 0} {t("common.credits")}</span>
            <BuyCreditsButton onSuccess={refresh}/>
          </div>
        </div>

        <label
          className="card-classic p-8 border-dashed border-2 border-[#E2DBCE] bg-white flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0F172A] transition-colors"
          data-testid="image-upload-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        >
          <Upload size={28} className="text-[#0F172A]"/>
          <div className="mt-3 font-serif-display text-xl">{t("img.dropTitle")}</div>
          <div className="text-sm text-[#64748B] mt-1">{t("img.dropSub")}</div>
          <input
            ref={inputRef}
            type="file"
            multiple accept="image/*"
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="image-upload-input"
          />
        </label>

        {items.length > 0 && (
          <div className="mt-8" data-testid="image-reorder-container">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-display text-2xl font-semibold">{t("img.reorder", { n: items.length })}</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setItems([])} className="border-[#E2DBCE]">{t("img.clear")}</Button>
                <Button
                  onClick={convert}
                  disabled={busy}
                  className="bg-[#0F172A] hover:bg-[#1E293B]"
                  data-testid="convert-images-to-pdf-button"
                >
                  {busy ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("img.converting")}</>) : (<><ImageIcon size={16} className="mr-2"/> {t("img.convert")}</>)}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((it, idx) => (
                <div key={it.id} className="card-classic p-3 flex flex-col" data-testid={`image-thumb-${idx}`}>
                  <div className="relative aspect-[4/5] rounded-md overflow-hidden bg-[#F4F0EA]">
                    <img src={it.url} alt={it.file.name} className="w-full h-full object-cover"/>
                    <span className="absolute top-2 left-2 pill">#{idx+1}</span>
                    <button
                      type="button"
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white border border-[#E2DBCE] grid place-items-center hover:bg-[#DC2626] hover:text-white"
                      onClick={() => removeAt(it.id)}
                      data-testid={`image-remove-${idx}`}
                      aria-label="Remove"
                    >
                      <X size={14}/>
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-[#64748B] truncate">{it.file.name}</div>
                  <div className="mt-2 flex items-center gap-1">
                    <Button size="sm" variant="outline" className="border-[#E2DBCE]" onClick={() => move(idx, -1)} data-testid={`image-move-left-${idx}`}>
                      {isAr ? <ArrowRight size={14}/> : <ArrowLeft size={14}/>}
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#E2DBCE]" onClick={() => move(idx, 1)} data-testid={`image-move-right-${idx}`}>
                      {isAr ? <ArrowLeft size={14}/> : <ArrowRight size={14}/>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div className="card-classic p-6 mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="convert-result-card">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#059669] font-mono-jb">{t("img.pdfReady")}</div>
              <div className="font-serif-display text-2xl font-semibold">{result.title}</div>
              <div className="text-sm text-[#475569]">{t("img.credits", { n: result.credits })}</div>
            </div>
            <a href={downloadUrl(result.download_url)} target="_blank" rel="noreferrer">
              <Button className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="download-generated-pdf-button">
                <Download size={16} className="mr-2"/> {t("img.downloadPdf")}
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
